/**
 * ProfileSection - 프로필 정보 표시 및 수정
 * Settings 페이지에서 사용자 프로필 정보를 표시하고 이름 수정 기능 제공
 */
import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useAuth } from "../../hooks/useAuth";
import { UPDATE_MY_PROFILE, type UpdateMyProfileData } from "../../lib/graphql/user";

export function ProfileSection() {
  const { user, refetch } = useAuth();
  const [name, setName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // 사용자 정보가 로드되면 이름 초기화
  useEffect(() => {
    if (user) {
      setName(user.name);
    }
  }, [user]);

  const [updateProfile, { loading }] = useMutation<UpdateMyProfileData>(UPDATE_MY_PROFILE, {
    onCompleted: (data) => {
      if (data.updateMyProfile.success) {
        setSuccess(true);
        setIsEditing(false);
        setError(null);
        refetch();
        // 2초 후 성공 메시지 숨김
        setTimeout(() => setSuccess(false), 2000);
      }
    },
    onError: (err) => {
      setError(err.message || "프로필 수정에 실패했습니다.");
    },
  });

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("이름을 입력해주세요.");
      return;
    }

    setError(null);
    await updateProfile({
      variables: {
        input: { name: trimmedName },
      },
    });
  };

  const handleCancel = () => {
    if (user) {
      setName(user.name);
    }
    setIsEditing(false);
    setError(null);
  };

  if (!user) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-24 rounded bg-gray-200" />
          <div className="h-10 w-full rounded bg-gray-200" />
          <div className="h-4 w-24 rounded bg-gray-200" />
          <div className="h-10 w-full rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* 섹션 헤더 */}
      <div className="border-b border-gray-200 px-4 py-3">
        <h2 className="text-base font-semibold text-gray-900">프로필 정보</h2>
      </div>

      <div className="p-4 space-y-4">
        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* 성공 메시지 */}
        {success && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3">
            <p className="text-sm text-green-600">프로필이 수정되었습니다.</p>
          </div>
        )}

        {/* 로그인 ID (읽기 전용) */}
        <div>
          <label htmlFor="loginId" className="block text-sm font-medium text-gray-700 mb-1">
            로그인 ID
          </label>
          <input
            id="loginId"
            type="text"
            value={user.id}
            disabled
            className="w-full min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        {/* 이름 (수정 가능) */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            이름
          </label>
          {isEditing ? (
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="이름을 입력하세요"
            />
          ) : (
            <div className="flex items-center gap-2">
              <input
                id="name-display"
                type="text"
                value={user.name}
                disabled
                className="flex-1 min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
              />
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="min-h-[44px] px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 active:bg-blue-100"
              >
                수정
              </button>
            </div>
          )}
        </div>

        {/* 소속 회사 (읽기 전용) */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
            소속 회사
          </label>
          <input
            id="company"
            type="text"
            value={user.companyName || "-"}
            disabled
            className="w-full min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        {/* 저장/취소 버튼 (수정 모드일 때만) */}
        {isEditing && (
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="flex-1 min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? "저장 중..." : "저장"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileSection;
